import { makeAutoObservable, observable, runInAction } from 'mobx';
import i18n from '../i18n';
import { createClientId, type ChatContentType, type ChatMessage } from '../components/chat';
import type {
  Conversation,
  InboxTab,
  MessageQuota,
  MessageService,
} from '../services/messageService';
import { createMockMessageService } from '../services/mockMessageService';

export const MESSAGE_PAGE_SIZE = 15;
const POLL_INTERVAL_MS = 30_000;

export type ThreadState = {
  messages: ChatMessage[];
  hasMore: boolean;
  loading: boolean;
  loadingEarlier: boolean;
};

const emptyThread: ThreadState = {
  messages: [],
  hasMore: false,
  loading: false,
  loadingEarlier: false,
};

type OutgoingContent = { contentType: ChatContentType; content: string; fileName?: string };

export const INBOX_TABS: InboxTab[] = ['all', 'unread', 'unreplied', 'history', 'starred', 'archived'];

/** Conversations without activity for this long move to the "历史" tab. */
const HISTORY_AFTER_MS = 7 * 24 * 60 * 60 * 1000;

function matchesTab(conversation: Conversation, tab: InboxTab) {
  if (tab === 'archived') {
    return conversation.archived;
  }
  if (conversation.archived) {
    return false;
  }
  switch (tab) {
    case 'unread':
      return conversation.unreadCount > 0;
    case 'unreplied':
      return conversation.lastMessageDirection === 'in';
    case 'history':
      return Date.now() - new Date(conversation.lastMessageAt).getTime() >= HISTORY_AFTER_MS;
    case 'starred':
      return conversation.starred;
    default:
      return true;
  }
}

function previewOf(payload: OutgoingContent) {
  if (payload.contentType === 'image') {
    return i18n.t('message.preview_image');
  }
  if (payload.contentType === 'file') {
    return i18n.t('message.preview_file', { name: payload.fileName ?? '' }).trim();
  }
  return payload.content;
}

/**
 * Conversation and message state is kept as plain objects replaced immutably,
 * so non-observer chat components receive new references whenever data changes.
 */
export default class MessageStore {
  conversations: Conversation[] = [];
  inboxLoading = false;
  tab: InboxTab = 'all';
  keyword = '';
  activeId: string | undefined = undefined;
  threads: Record<string, ThreadState> = {};
  quota: MessageQuota | null = null;

  private service: MessageService;
  private pollTimer: ReturnType<typeof setInterval> | undefined;

  constructor(service: MessageService = createMockMessageService()) {
    this.service = service;
    makeAutoObservable<MessageStore, 'service' | 'pollTimer'>(
      this,
      {
        conversations: observable.ref,
        threads: observable.shallow,
        quota: observable.ref,
        service: false,
        pollTimer: false,
      },
      { autoBind: true },
    );
  }

  get visibleConversations() {
    const q = this.keyword.trim().toLowerCase();
    return this.conversations
      .filter((item) => matchesTab(item, this.tab))
      .filter(
        (item) =>
          !q ||
          item.peer.name.toLowerCase().includes(q) ||
          item.handle.toLowerCase().includes(q) ||
          item.lastMessagePreview.toLowerCase().includes(q),
      )
      .slice()
      .sort((a, b) => b.lastMessageAt.localeCompare(a.lastMessageAt));
  }

  get tabCounts(): Record<InboxTab, number> {
    const counts = {} as Record<InboxTab, number>;
    INBOX_TABS.forEach((tab) => {
      counts[tab] = this.conversations.filter((item) => matchesTab(item, tab)).length;
    });
    return counts;
  }

  get unreadTotal() {
    return this.conversations
      .filter((item) => !item.archived)
      .reduce((sum, item) => sum + item.unreadCount, 0);
  }

  get activeConversation() {
    return this.conversations.find((item) => item.id === this.activeId);
  }

  get activeThread(): ThreadState {
    return (this.activeId && this.threads[this.activeId]) || emptyThread;
  }

  get sendBlockedReason(): string | undefined {
    const conversation = this.activeConversation;
    if (!conversation) {
      return undefined;
    }
    if (conversation.archived) {
      return i18n.t('message.blocked_archived');
    }
    if (this.quota && this.quota.usedToday >= this.quota.dailyLimit) {
      return i18n.t('message.blocked_quota', { limit: this.quota.dailyLimit });
    }
    const limit = this.quota?.perTalentLimit ?? 5;
    if (conversation.outgoingSinceReply >= limit) {
      return i18n.t('message.blocked_limit', {
        limit,
        sent: conversation.outgoingSinceReply,
      });
    }
    return undefined;
  }

  setTab(tab: InboxTab) {
    this.tab = tab;
  }

  setKeyword(keyword: string) {
    this.keyword = keyword;
  }

  async loadInbox(options: { silent?: boolean } = {}) {
    if (!options.silent) {
      this.inboxLoading = true;
    }
    try {
      const [conversations, quota] = await Promise.all([
        this.service.listConversations(),
        this.service.getQuota(),
      ]);
      runInAction(() => {
        this.conversations = conversations;
        this.quota = quota;
      });
    } finally {
      runInAction(() => {
        this.inboxLoading = false;
      });
    }
  }

  async select(id: string) {
    if (this.activeId === id && this.threads[id]) {
      return;
    }
    this.activeId = id;
    if (!this.threads[id]) {
      await this.loadLatest(id);
    }
    const conversation = this.conversations.find((item) => item.id === id);
    if (conversation && conversation.unreadCount > 0) {
      this.patchConversation(id, { unreadCount: 0 });
      await this.service.markRead(id);
    }
  }

  async loadEarlier() {
    const id = this.activeId;
    if (!id) {
      return;
    }
    const thread = this.threads[id];
    if (!thread || !thread.hasMore || thread.loadingEarlier || thread.messages.length === 0) {
      return;
    }
    this.setThread(id, { ...thread, loadingEarlier: true });
    try {
      const page = await this.service.listMessages({
        conversationId: id,
        beforeId: thread.messages[0].id,
        size: MESSAGE_PAGE_SIZE,
      });
      runInAction(() => {
        const current = this.threads[id] ?? thread;
        this.setThread(id, {
          ...current,
          messages: [...page.list, ...current.messages],
          hasMore: page.hasMore,
          loadingEarlier: false,
        });
      });
    } catch (e) {
      runInAction(() => {
        const current = this.threads[id] ?? thread;
        this.setThread(id, { ...current, loadingEarlier: false });
      });
      throw e;
    }
  }

  async send(payload: OutgoingContent) {
    const id = this.activeId;
    if (!id || this.sendBlockedReason) {
      return;
    }
    const clientId = createClientId();
    const optimistic: ChatMessage = {
      id: clientId,
      clientId,
      direction: 'out',
      contentType: payload.contentType,
      content: payload.content,
      fileName: payload.fileName,
      createdAt: new Date().toISOString(),
      status: 'sending',
    };
    this.appendMessage(id, optimistic);
    const conversation = this.conversations.find((item) => item.id === id);
    this.patchConversation(id, {
      lastMessagePreview: previewOf(payload),
      lastMessageAt: optimistic.createdAt,
      lastMessageDirection: 'out',
      messageCount: (conversation?.messageCount ?? 0) + 1,
      outgoingSinceReply: (conversation?.outgoingSinceReply ?? 0) + 1,
    });
    await this.deliver(id, optimistic);
  }

  async resend(message: ChatMessage) {
    const id = this.activeId;
    if (!id || message.status !== 'failed' || !message.clientId) {
      return;
    }
    const retry: ChatMessage = { ...message, status: 'sending' };
    this.replaceMessage(id, message.clientId, retry);
    this.adjustOutgoingSinceReply(id, 1);
    await this.deliver(id, retry);
  }

  async setArchived(id: string, archived: boolean) {
    this.patchConversation(id, { archived });
    await this.service.setArchived(id, archived);
  }

  async setStarred(id: string, starred: boolean) {
    this.patchConversation(id, { starred });
    await this.service.setStarred(id, starred);
  }

  /** Picks up new inbound messages; only refetches threads whose last message changed. */
  async refresh() {
    await this.loadInbox({ silent: true });
    const id = this.activeId;
    const conversation = this.activeConversation;
    const thread = id ? this.threads[id] : undefined;
    if (!id || !conversation || !thread) {
      return;
    }
    const last = thread.messages[thread.messages.length - 1];
    if (last && last.createdAt >= conversation.lastMessageAt) {
      return;
    }
    const page = await this.service.listMessages({ conversationId: id, size: MESSAGE_PAGE_SIZE });
    runInAction(() => {
      const current = this.threads[id] ?? thread;
      const known = new Set(current.messages.map((item) => item.id));
      const fresh = page.list.filter((item) => !known.has(item.id));
      if (fresh.length > 0) {
        this.setThread(id, { ...current, messages: [...current.messages, ...fresh] });
      }
    });
  }

  startPolling() {
    this.stopPolling();
    this.pollTimer = setInterval(() => {
      this.refresh().catch(() => undefined);
    }, POLL_INTERVAL_MS);
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = undefined;
    }
  }

  private async loadLatest(id: string) {
    this.setThread(id, { ...emptyThread, loading: true });
    try {
      const page = await this.service.listMessages({ conversationId: id, size: MESSAGE_PAGE_SIZE });
      runInAction(() => {
        this.setThread(id, {
          messages: page.list,
          hasMore: page.hasMore,
          loading: false,
          loadingEarlier: false,
        });
      });
    } catch (e) {
      runInAction(() => {
        this.setThread(id, { ...emptyThread });
      });
      throw e;
    }
  }

  private async deliver(id: string, pending: ChatMessage) {
    const clientId = pending.clientId as string;
    try {
      const saved = await this.service.sendMessage({
        conversationId: id,
        clientId,
        contentType: pending.contentType,
        content: pending.content,
        fileName: pending.fileName,
      });
      runInAction(() => {
        this.replaceMessage(id, clientId, { ...saved, clientId });
        if (this.quota) {
          this.quota = { ...this.quota, usedToday: this.quota.usedToday + 1 };
        }
      });
    } catch {
      runInAction(() => {
        this.replaceMessage(id, clientId, { ...pending, status: 'failed' });
        this.adjustOutgoingSinceReply(id, -1);
      });
    }
  }

  private adjustOutgoingSinceReply(id: string, delta: number) {
    const conversation = this.conversations.find((item) => item.id === id);
    if (conversation) {
      this.patchConversation(id, {
        outgoingSinceReply: Math.max(0, conversation.outgoingSinceReply + delta),
      });
    }
  }

  private setThread(id: string, thread: ThreadState) {
    this.threads[id] = thread;
  }

  private appendMessage(id: string, message: ChatMessage) {
    const thread = this.threads[id] ?? emptyThread;
    this.setThread(id, { ...thread, messages: [...thread.messages, message] });
  }

  private replaceMessage(id: string, clientId: string, next: ChatMessage) {
    const thread = this.threads[id];
    if (!thread) {
      return;
    }
    this.setThread(id, {
      ...thread,
      messages: thread.messages.map((item) => (item.clientId === clientId ? next : item)),
    });
  }

  private patchConversation(id: string, patch: Partial<Conversation>) {
    this.conversations = this.conversations.map((item) =>
      item.id === id ? { ...item, ...patch } : item,
    );
  }
}
